// src/components/ui/Layout/Col.tsx
// Micro layout: Responsive column following IonicLayout.md

import React, { forwardRef } from 'react';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export type ColumnSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 'auto';
export type ColAlign = 'start' | 'center' | 'end' | 'stretch';

export interface ColProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Size on mobile (< 768px). Required. */
  mobile: ColumnSize;
  /** Size on tablet (768px - 991px). Falls back to mobile. */
  tablet?: ColumnSize;
  /** Size on desktop (≥ 992px). Falls back to tablet, then mobile. */
  desktop?: ColumnSize;
  /** Override row alignment for this column */
  alignSelf?: ColAlign;
  children?: React.ReactNode;
}

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

// Breakpoints matching Ionic's defaults
const BREAKPOINTS = {
  tablet: 768,
  desktop: 992,
};

const alignMap: Record<ColAlign, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
};

// ═══════════════════════════════════════════════════════════════════
// HELPER
// ═══════════════════════════════════════════════════════════════════

const getWidth = (size: ColumnSize): string => {
  if (size === 'auto') return 'auto';
  return `${(size / 12) * 100}%`;
};

const getFlex = (size: ColumnSize): string => {
  if (size === 'auto') return '0 0 auto';
  return `0 0 ${getWidth(size)}`;
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const Col = forwardRef<HTMLDivElement, ColProps>(
  (
    {
      mobile,
      tablet,
      desktop,
      alignSelf,
      children,
      className = '',
      style,
      ...rest
    },
    ref
  ) => {
    // Cascade: desktop falls back to tablet falls back to mobile
    const effectiveTablet = tablet ?? mobile;
    const effectiveDesktop = desktop ?? effectiveTablet;

    // Generate CSS custom properties for responsive sizing
    // We use CSS media queries inline via a style element approach
    // For SSR compatibility and simplicity, we use a data attribute approach
    // that can be styled via CSS, but for inline we compute base mobile style

    const baseStyle: React.CSSProperties = {
      flex: getFlex(mobile),
      maxWidth: mobile === 'auto' ? undefined : getWidth(mobile),
      alignSelf: alignSelf ? alignMap[alignSelf] : undefined,
      boxSizing: 'border-box',
      ...style,
    };

    // We need CSS for responsive breakpoints
    // Using CSS custom properties for this
    const cssVars = {
      '--col-mobile-flex': getFlex(mobile),
      '--col-mobile-max-width': mobile === 'auto' ? 'none' : getWidth(mobile),
      '--col-tablet-flex': getFlex(effectiveTablet),
      '--col-tablet-max-width': effectiveTablet === 'auto' ? 'none' : getWidth(effectiveTablet),
      '--col-desktop-flex': getFlex(effectiveDesktop),
      '--col-desktop-max-width': effectiveDesktop === 'auto' ? 'none' : getWidth(effectiveDesktop),
    } as React.CSSProperties;

    return (
      <div
        ref={ref}
        style={{ ...baseStyle, ...cssVars }}
        className={`layout-col ${className}`}
        data-layout="Col"
        data-mobile={mobile}
        data-tablet={effectiveTablet}
        data-desktop={effectiveDesktop}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

Col.displayName = 'Col';

export default Col;

// ═══════════════════════════════════════════════════════════════════
// REQUIRED CSS (add to global styles or Layout.css)
// ═══════════════════════════════════════════════════════════════════
/*
.layout-col {
  flex: var(--col-mobile-flex);
  max-width: var(--col-mobile-max-width);
}

@media (min-width: 768px) {
  .layout-col {
    flex: var(--col-tablet-flex);
    max-width: var(--col-tablet-max-width);
  }
}

@media (min-width: 992px) {
  .layout-col {
    flex: var(--col-desktop-flex);
    max-width: var(--col-desktop-max-width);
  }
}
*/
